import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Trash2, Plus } from "lucide-react";

const DAYS = [
  { k: "mon", label: "Mon" }, { k: "tue", label: "Tue" }, { k: "wed", label: "Wed" },
  { k: "thu", label: "Thu" }, { k: "fri", label: "Fri" }, { k: "sat", label: "Sat" }, { k: "sun", label: "Sun" },
];

export default function AvailabilityPage() {
  const [sched, setSched] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [exc, setExc] = useState({ date: "", type: "holiday", start_time: "", end_time: "", reason: "" });

  useEffect(() => { refresh(); }, []);
  async function refresh() {
    const [s, e] = await Promise.all([api.get("/schedule"), api.get("/schedule/exceptions")]);
    setSched(s.data);
    setExceptions(e.data);
  }

  function setDay(day, patch) {
    setSched({ ...sched, working_days: { ...sched.working_days, [day]: { ...sched.working_days[day], ...patch } } });
  }

  async function save() {
    try {
      const payload = {
        working_days: sched.working_days,
        slot_duration_min: Number(sched.slot_duration_min),
        buffer_min: Number(sched.buffer_min),
        booking_notice_hours: Number(sched.booking_notice_hours),
        max_advance_days: Number(sched.max_advance_days),
        timezone: sched.timezone,
      };
      await api.put("/schedule", payload);
      toast.success("Schedule saved");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function addExc() {
    if (!exc.date) return toast.error("Date required");
    try {
      await api.post("/schedule/exceptions", {
        date: exc.date, type: exc.type,
        start_time: exc.start_time || null, end_time: exc.end_time || null,
        reason: exc.reason || null,
      });
      setExc({ date: "", type: "holiday", start_time: "", end_time: "", reason: "" });
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function delExc(id) {
    await api.delete(`/schedule/exceptions/${id}`);
    refresh();
  }

  if (!sched) return <div className="text-slate-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="availability-page">
      <div>
        <h1 className="text-2xl font-display font-bold text-slate-900">Availability</h1>
        <p className="text-slate-500 text-sm">Working hours, slot duration, holidays and blocked times</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="font-semibold text-slate-800 mb-4">Working days &amp; hours</div>
        <div className="space-y-2">
          {DAYS.map((d) => {
            const cfg = sched.working_days[d.k] || { enabled: false, start: "09:00", end: "17:00", breaks: [] };
            return (
              <div key={d.k} className="flex items-center gap-3 flex-wrap py-1.5 border-b border-slate-100 last:border-0">
                <label className="flex items-center gap-2 w-24">
                  <input type="checkbox" checked={cfg.enabled} onChange={(e) => setDay(d.k, { enabled: e.target.checked })} />
                  <span className="font-medium text-slate-800">{d.label}</span>
                </label>
                <input type="time" value={cfg.start} onChange={(e) => setDay(d.k, { start: e.target.value })} disabled={!cfg.enabled} className="input w-36" />
                <span className="text-slate-500">to</span>
                <input type="time" value={cfg.end} onChange={(e) => setDay(d.k, { end: e.target.value })} disabled={!cfg.enabled} className="input w-36" />
                <span className="text-xs text-slate-500">Breaks:</span>
                {(cfg.breaks || []).map((b, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <input type="time" value={b.start} onChange={(e) => {
                      const nb = [...cfg.breaks]; nb[i] = { ...b, start: e.target.value }; setDay(d.k, { breaks: nb });
                    }} className="input w-32" />
                    <span>-</span>
                    <input type="time" value={b.end} onChange={(e) => {
                      const nb = [...cfg.breaks]; nb[i] = { ...b, end: e.target.value }; setDay(d.k, { breaks: nb });
                    }} className="input w-32" />
                    <button onClick={() => setDay(d.k, { breaks: cfg.breaks.filter((_, j) => j !== i) })} className="text-rose-600"><Trash2 size={14} /></button>
                  </div>
                ))}
                <button onClick={() => setDay(d.k, { breaks: [...(cfg.breaks || []), { start: "13:00", end: "14:00" }] })} className="text-xs text-emerald-700 font-medium">+ Break</button>
              </div>
            );
          })}
        </div>
        <div className="mt-5 grid sm:grid-cols-4 gap-3">
          <Num label="Slot duration (min)" value={sched.slot_duration_min} onChange={(v) => setSched({ ...sched, slot_duration_min: v })} />
          <Num label="Buffer between (min)" value={sched.buffer_min} onChange={(v) => setSched({ ...sched, buffer_min: v })} />
          <Num label="Booking notice (hrs)" value={sched.booking_notice_hours} onChange={(v) => setSched({ ...sched, booking_notice_hours: v })} />
          <Num label="Max advance (days)" value={sched.max_advance_days} onChange={(v) => setSched({ ...sched, max_advance_days: v })} />
        </div>
        <button onClick={save} className="mt-5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl px-5 py-2.5 font-semibold text-sm" data-testid="save-schedule-btn">Save schedule</button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="font-semibold text-slate-800 mb-4">Holidays, leave &amp; blocked slots</div>
        <div className="grid sm:grid-cols-6 gap-2 items-start">
          <label className="col-span-2"><div className="text-xs text-slate-500 mb-1">Date</div><input type="date" value={exc.date} onChange={(e) => setExc({ ...exc, date: e.target.value })} className="input w-full" /></label>
          <label><div className="text-xs text-slate-500 mb-1">Type</div><select value={exc.type} onChange={(e) => setExc({ ...exc, type: e.target.value })} className="input w-full"><option value="holiday">Holiday</option><option value="leave">Leave</option><option value="block">Block</option></select></label>
          <label><div className="text-xs text-slate-500 mb-1">Start</div><input type="time" value={exc.start_time} onChange={(e) => setExc({ ...exc, start_time: e.target.value })} className="input w-full" /></label>
          <label><div className="text-xs text-slate-500 mb-1">End</div><input type="time" value={exc.end_time} onChange={(e) => setExc({ ...exc, end_time: e.target.value })} className="input w-full" /></label>
          <button onClick={addExc} className="bg-slate-900 text-white rounded-lg px-3 py-2 text-sm font-semibold flex items-center justify-center gap-1" data-testid="add-exception-btn"><Plus size={14} /> Add</button>
        </div>
        <div className="mt-4 space-y-2">
          {exceptions.map((e) => (
            <div key={e.id} className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
              <div className="text-sm">
                <span className="font-semibold capitalize">{e.type}</span> · <span className="text-slate-700">{e.date}</span>
                {e.start_time && <span className="text-slate-500"> · {e.start_time}–{e.end_time}</span>}
                {e.reason && <span className="text-slate-500"> · {e.reason}</span>}
              </div>
              <button onClick={() => delExc(e.id)} className="text-rose-600"><Trash2 size={14} /></button>
            </div>
          ))}
          {exceptions.length === 0 && <div className="text-sm text-slate-500">No exceptions set.</div>}
        </div>
      </div>
      <style>{`.input{padding:.5rem .7rem;border:1px solid #CBD5E1;border-radius:.6rem;background:#fff;color:#0F172A;font-size:.9rem;}
      .input:focus{outline:none;border-color:#0D9488;box-shadow:0 0 0 3px rgb(13 148 136 / 0.15)}
      .input:disabled{background:#F8FAFC;color:#94A3B8}`}</style>
    </div>
  );
}

function Num({ label, value, onChange }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="input w-full" />
    </label>
  );
}
